import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Edit2, Plus, Trash2 } from 'lucide-react'
import { featureFlagsApi } from '../../lib/api'
import DataTable, { Column } from '../../components/shared/DataTable'
import DetailDrawer from '../../components/shared/DetailDrawer'
import ConfirmDialog from '../../components/shared/ConfirmDialog'
import LoadingState from '../../components/shared/LoadingState'
import ErrorState from '../../components/shared/ErrorState'
import { useToast } from '../../hooks/useToast'
import { usePermission } from '../../hooks/usePermission'
import { formatDate } from '../../lib/utils'
import type { FeatureFlag, FeatureFlagOverride } from '../../types'
import NoticeBanner from '../../components/shared/NoticeBanner'
import { tx } from '../../lib/i18n'

const CORE_FLAG_KEYS = ['liff-booking', 'waitlist', 'reviews', 'online-payment', 'ai-center']

const allPlans = ['starter', 'pro', 'business', 'enterprise']

const FeatureFlagsPage: React.FC = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const { can } = usePermission()

  const [editingFlag, setEditingFlag] = useState<FeatureFlag | null>(null)
  const [overridesFlagId, setOverridesFlagId] = useState<string | null>(null)
  const [toggleConfirm, setToggleConfirm] = useState<{ flag: FeatureFlag; newVal: boolean } | null>(null)
  const [addOverrideForm, setAddOverrideForm] = useState({ shopId: '', shopName: '', isEnabled: true, reason: '' })
  const [editForm, setEditForm] = useState<{ rolloutPercentage: number; internalNote: string; enabledForPlans: string[] }>({ rolloutPercentage: 0, internalNote: '', enabledForPlans: [] })

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['feature-flags'],
    queryFn: () => featureFlagsApi.list().then((r) => r.data.data),
  })

  const flags = data ?? []
  // derive from the live list so the drawer shows overrides right after add/remove
  const overridesFlag = flags.find((f) => f.id === overridesFlagId) ?? null
  const setOverridesFlag = (f: FeatureFlag | null) => setOverridesFlagId(f ? f.id : null)

  const toggleMutation = useMutation({
    mutationFn: ({ id, isEnabled }: { id: string; isEnabled: boolean }) =>
      featureFlagsApi.update(id, { isEnabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] })
      showToast({ type: 'success', message: tx('Feature flag updated.') })
      setToggleConfirm(null)
    },
    onError: () => showToast({ type: 'error', message: tx('Failed to update flag.') }),
  })

  const updateMutation = useMutation({
    mutationFn: () => featureFlagsApi.update(editingFlag!.id, editForm),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] })
      showToast({ type: 'success', message: tx('Flag settings saved.') })
      setEditingFlag(null)
    },
    onError: () => showToast({ type: 'error', message: tx('Failed to save.') }),
  })

  const addOverrideMutation = useMutation({
    mutationFn: () =>
      featureFlagsApi.addOverride(overridesFlag!.id, {
        shopId: addOverrideForm.shopId,
        isEnabled: addOverrideForm.isEnabled,
        reason: addOverrideForm.reason,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] })
      showToast({ type: 'success', message: tx('Override added.') })
      setAddOverrideForm({ shopId: '', shopName: '', isEnabled: true, reason: '' })
    },
    onError: () => showToast({ type: 'error', message: tx('Failed to add override.') }),
  })

  const removeOverrideMutation = useMutation({
    mutationFn: ({ flagId, overrideId }: { flagId: string; overrideId: string }) =>
      featureFlagsApi.removeOverride(flagId, overrideId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] })
      showToast({ type: 'success', message: tx('Override removed.') })
    },
  })

  const overrideColumns: Column<FeatureFlagOverride>[] = [
    { key: 'shopName', header: tx('Shop'), render: (r) => <span className="font-medium text-gray-900">{r.shopName}</span> },
    {
      key: 'isEnabled', header: tx('Override'),
      render: (r) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${r.isEnabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {r.isEnabled ? tx('Enabled') : tx('Disabled')}
        </span>
      ),
    },
    { key: 'reason', header: tx('Reason'), render: (r) => <span className="text-gray-500 text-sm">{r.reason ?? '—'}</span> },
    { key: 'createdAt', header: tx('Added'), render: (r) => <span className="text-gray-400 text-sm">{formatDate(r.createdAt)}</span> },
    {
      key: 'actions', header: '',
      render: (r) => (
        can('feature-flags.override') && <button
          onClick={() => overridesFlag && removeOverrideMutation.mutate({ flagId: overridesFlag.id, overrideId: r.shopId })}
          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      ),
    },
  ]

  if (isLoading) return <LoadingState variant="spinner" message={tx('Loading feature flags...')} />
  if (error) return <ErrorState message={tx('Failed to load feature flags.')} onRetry={() => refetch()} />

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-gray-900">{tx('Feature Flags')}</h1>
        <p className="text-sm text-gray-500 mt-0.5">{tx('Control feature availability across plans and individual shops')}</p>
      </div>

      <NoticeBanner tone="ok">
        <b>เชื่อมกับแอปร้านแล้ว</b> — queza-core อ่านค่า flag ที่มีป้าย <b>core</b> (<span className="font-mono">liff-booking · waitlist · reviews · online-payment · ai-center</span>) ภายใน 30 วินาที — ปิด = ฟีเจอร์นั้นหยุดทำงานในร้านที่ตรงเงื่อนไข (แพ็กเกจ / % / กำหนดเฉพาะร้าน) · key อื่นยังไม่มีผลกับแอปร้าน
      </NoticeBanner>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gray-50">
            <tr>
              {['Feature', 'Key', 'Status', 'Rollout', 'Plans', 'Overrides', 'Actions'].map((h) => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {flags.map((flag, idx) => (
              <tr key={flag.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{flag.displayName}</p>
                  {flag.description && <p className="text-xs text-gray-400 mt-0.5">{flag.description}</p>}
                </td>
                <td className="px-4 py-3">
                  <span className="font-mono text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{flag.key}</span>
                  {CORE_FLAG_KEYS.includes(flag.key) && <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700" title="queza-core อ่านค่านี้">core</span>}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => setToggleConfirm({ flag, newVal: !flag.isEnabled })}
                    disabled={!can('feature-flags.update')}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${flag.isEnabled ? 'bg-indigo-600' : 'bg-gray-200'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${flag.isEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${flag.rolloutPercentage}%` }} />
                    </div>
                    <span className="text-xs text-gray-500">{flag.rolloutPercentage}%</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {flag.enabledForPlans.length === 0 ? (
                      <span className="text-xs text-gray-400">{tx('None')}</span>
                    ) : (
                      flag.enabledForPlans.map((p) => (
                        <span key={p} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">{p}</span>
                      ))
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => setOverridesFlag(flag)}
                    className={`text-xs font-medium px-2 py-1 rounded-lg transition-colors ${flag.overrides.length > 0 ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                  >
                    {flag.overrides.length} override{flag.overrides.length !== 1 ? 's' : ''}
                  </button>
                </td>
                <td className="px-4 py-3">
                  {can('feature-flags.update') && (
                  <button
                    onClick={() => { setEditingFlag(flag); setEditForm({ rolloutPercentage: flag.rolloutPercentage, internalNote: flag.internalNote ?? '', enabledForPlans: [...flag.enabledForPlans] }) }}
                    className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Toggle confirm */}
      <ConfirmDialog
        open={!!toggleConfirm}
        onClose={() => setToggleConfirm(null)}
        onConfirm={() => toggleConfirm && toggleMutation.mutate({ id: toggleConfirm.flag.id, isEnabled: toggleConfirm.newVal })}
        title={toggleConfirm?.newVal ? `Enable "${toggleConfirm?.flag.displayName}"?` : `Disable "${toggleConfirm?.flag.displayName}"?`}
        description={toggleConfirm?.newVal ? 'This will enable the feature for all eligible users.' : 'This will turn off the feature globally. Shop overrides may still apply.'}
        confirmLabel={toggleConfirm?.newVal ? 'Enable' : 'Disable'}
        variant={toggleConfirm?.newVal ? 'default' : 'warning'}
        isLoading={toggleMutation.isPending}
      />

      {/* Edit modal */}
      {editingFlag && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setEditingFlag(null)} />
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">{tx('Edit Flag —')} {editingFlag.displayName}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Rollout Percentage')}</label>
                <div className="flex items-center gap-3">
                  <input
                    type="range" min="0" max="100"
                    value={editForm.rolloutPercentage}
                    onChange={(e) => setEditForm((f) => ({ ...f, rolloutPercentage: parseInt(e.target.value) }))}
                    className="flex-1"
                  />
                  <span className="text-sm font-medium text-gray-700 w-10 text-right">{editForm.rolloutPercentage}%</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{tx('Enabled For Plans')}</label>
                <div className="flex flex-wrap gap-2">
                  {allPlans.map((p) => (
                    <label key={p} className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editForm.enabledForPlans.includes(p)}
                        onChange={(e) => setEditForm((f) => ({
                          ...f,
                          enabledForPlans: e.target.checked
                            ? [...f.enabledForPlans, p]
                            : f.enabledForPlans.filter((x) => x !== p),
                        }))}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                      />
                      <span className="text-sm text-gray-700 capitalize">{p}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Internal Note')}</label>
                <textarea
                  rows={2}
                  value={editForm.internalNote}
                  onChange={(e) => setEditForm((f) => ({ ...f, internalNote: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  placeholder={tx('Internal notes...')}
                />
              </div>
            </div>
            <div className="flex gap-3 mt-5 justify-end">
              <button onClick={() => setEditingFlag(null)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">{tx('Cancel')}</button>
              <button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {updateMutation.isPending ? tx('Saving...') : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shop Overrides Drawer */}
      <DetailDrawer
        open={!!overridesFlag}
        onClose={() => setOverridesFlag(null)}
        title={`Overrides — ${overridesFlag?.displayName}`}
        subtitle={`${overridesFlag?.overrides.length ?? 0} shop-level overrides`}
        width="lg"
      >
        {overridesFlag && (
          <div className="space-y-5">
            {/* Add override */}
            {can('feature-flags.override') && (
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Plus className="h-4 w-4" /> {tx('Add Override')}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{tx('Shop ID')}</label>
                  <input type="text" placeholder="shop-123" value={addOverrideForm.shopId} onChange={(e) => setAddOverrideForm((f) => ({ ...f, shopId: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{tx('Override')}</label>
                  <select value={addOverrideForm.isEnabled ? 'true' : 'false'} onChange={(e) => setAddOverrideForm((f) => ({ ...f, isEnabled: e.target.value === 'true' }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    <option value="true">{tx('Enabled')}</option>
                    <option value="false">{tx('Disabled')}</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">{tx('Reason')}</label>
                  <input type="text" placeholder={tx('Reason...')} value={addOverrideForm.reason} onChange={(e) => setAddOverrideForm((f) => ({ ...f, reason: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
              </div>
              <button
                onClick={() => addOverrideMutation.mutate()}
                disabled={!addOverrideForm.shopId || addOverrideMutation.isPending}
                className="mt-3 w-full py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {addOverrideMutation.isPending ? 'Adding...' : tx('Add Override')}
              </button>
            </div>
            )}
            <DataTable
              columns={overrideColumns}
              data={overridesFlag.overrides}
              emptyMessage={tx('No overrides configured.')}
            />
          </div>
        )}
      </DetailDrawer>
    </div>
  )
}

export default FeatureFlagsPage
